import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Loader2, MapPin, SlidersHorizontal, X } from 'lucide-react';
import * as Lucide from 'lucide-react';
import { PropertyCard } from '@/components/PropertyCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSiteData } from '@/context/SiteDataContext';
import { useLanguage } from '@/context/LanguageContext';
import { resolveMediaUrl } from '@/lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICE_BUCKETS = [
  { label: 'Under ₹20 L', min: 0, max: 2000000 },
  { label: '₹20 – 25 L', min: 2000000, max: 2500000 },
  { label: '₹25 – 35 L', min: 2500000, max: 3500000 },
  { label: '₹35 – 50 L', min: 3500000, max: 5000000 },
  { label: '₹50 L – 1 Cr', min: 5000000, max: 10000000 },
  { label: '₹1 Cr +', min: 10000000, max: 1e12 },
];

const FACING_OPTIONS = ['Any', 'North', 'South', 'East', 'West', 'North-East', 'North-West', 'South-East', 'South-West', 'Corner'];
const STATUS_OPTIONS = ['Any', 'Available', 'Booked', 'Sold', 'Coming Soon', 'Ready to Move', 'Under Construction'];

export default function CategoryPage({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const { categories, siteSettings } = useSiteData();
  const { t, lang, pickLocale } = useLanguage();
  const category = useMemo(() => categories.find((c) => c.slug === slug), [categories, slug]);

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    location: 'All',
    bucket: 'Any',
    facing: 'Any',
    status: 'Any',
    bhk: 'Any',
    minArea: '',
    maxArea: '',
    search: '',
  });

  useEffect(() => {
    if (!category) return;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ category: category.name_en, per_page: '60' });
        const { data } = await axios.get(`${API}/properties?${params.toString()}`);
        setProperties(data.properties || data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [category]);

  const locations = useMemo(() => {
    const set = new Set(properties.map((p) => p.location_label).filter(Boolean));
    return ['All', ...Array.from(set).sort()];
  }, [properties]);

  const bhkOptions = useMemo(() => {
    const set = new Set(properties.map((p) => p.bhk).filter((b) => b && b !== 'NA'));
    return ['Any', ...Array.from(set).sort()];
  }, [properties]);

  const filtered = useMemo(() => {
    return properties.filter((p) => {
      if (filters.location !== 'All' && p.location_label !== filters.location) return false;
      if (filters.facing !== 'Any' && p.plot_facing !== filters.facing) return false;
      if (filters.status !== 'Any' && p.status !== filters.status) return false;
      if (filters.bhk !== 'Any' && p.bhk !== filters.bhk) return false;
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const hay = `${p.title} ${p.code} ${p.locality || ''} ${p.location_label || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const area = p.plot_area_sqft || p.house_area_sqft || p.builtup_area_sqft || p.carpet_area_sqft;
      if (filters.minArea && area && area < Number(filters.minArea)) return false;
      if (filters.maxArea && area && area > Number(filters.maxArea)) return false;
      if (filters.bucket !== 'Any') {
        const bucket = PRICE_BUCKETS.find((b) => b.label === filters.bucket);
        const price = p.total_price || (p.rate_per_sqft && area ? p.rate_per_sqft * area : null);
        if (!price) return false;
        if (price < bucket.min || price >= bucket.max) return false;
      }
      return true;
    });
  }, [properties, filters]);

  const resetFilters = () => setFilters({ location: 'All', bucket: 'Any', facing: 'Any', status: 'Any', bhk: 'Any', minArea: '', maxArea: '', search: '' });

  if (!category) {
    return (
      <div className="pt-20 min-h-[60vh] flex items-center justify-center">
        {categories.length === 0 ? (
          <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
        ) : (
          <p className="text-gray-500">Category not found</p>
        )}
      </div>
    );
  }

  const Icon = Lucide[category.icon] || Lucide.Building2;
  const heroImg = resolveMediaUrl(category.hero_image || category.thumbnail_url);
  const title = pickLocale(category, 'name', category.name_en);
  const desc = pickLocale(category, 'short_description', category.short_description_en) || '';
  const metaTitle = category.meta_title || `${title} | ${siteSettings?.site_title_en || 'Shree Bajrang Developers'}`;
  const metaDesc = category.meta_description || desc;
  const ogImage = resolveMediaUrl(category.hero_image || category.thumbnail_url || siteSettings?.default_share_image);

  // Schema markup for SEO
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": metaTitle,
    "numberOfItems": filtered.length,
    "itemListElement": filtered.slice(0, 20).map((p, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "url": `${typeof window !== 'undefined' ? window.location.origin : ''}/property/${p.id}`,
      "name": p.title,
    })),
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>

      <div className="pt-16 lg:pt-20" data-testid={`category-page-${slug}`}>
        {/* Hero */}
        <section className="relative h-64 md:h-80 overflow-hidden">
          {heroImg ? (
            <img src={heroImg} alt={title} className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-brand-rust to-brand-orange" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />
          <div className="relative z-10 max-w-7xl mx-auto h-full px-4 lg:px-8 flex flex-col justify-end pb-8">
            <Badge className="w-fit bg-white/20 backdrop-blur text-white border border-white/30 mb-3">
              <Icon className="h-3 w-3 mr-1" />
              {filtered.length} {filtered.length === 1 ? 'listing' : 'listings'}
            </Badge>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-2">{title}</h1>
            {desc && <p className="text-white/90 max-w-2xl">{desc}</p>}
          </div>
        </section>

        {/* Filters bar */}
        <section className="sticky top-16 lg:top-20 z-30 bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3 flex flex-wrap items-center gap-3">
            <Input
              type="search"
              placeholder={lang === 'hi' ? 'खोजें (शीर्षक, स्थान)' : 'Search title or location'}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="max-w-xs"
              data-testid="cat-search"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters((v) => !v)}
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal className="h-4 w-4 mr-2" /> {t('common.filters', 'Filters')}
            </Button>
            <Button variant="ghost" size="sm" onClick={resetFilters} className="ml-auto">
              <X className="h-4 w-4 mr-1" /> {t('common.reset', 'Reset')}
            </Button>
          </div>

          {showFilters && (
            <div className="border-t bg-gray-50">
              <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <FilterSelect label={t('common.location', 'Location')} value={filters.location} onChange={(v) => setFilters({ ...filters, location: v })} options={locations} />
                <FilterSelect label="Price" value={filters.bucket} onChange={(v) => setFilters({ ...filters, bucket: v })} options={['Any', ...PRICE_BUCKETS.map((b) => b.label)]} />
                <FilterSelect label={t('common.facing', 'Facing')} value={filters.facing} onChange={(v) => setFilters({ ...filters, facing: v })} options={FACING_OPTIONS} />
                <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={STATUS_OPTIONS} />
                {bhkOptions.length > 1 && (
                  <FilterSelect label={t('common.bhk', 'BHK')} value={filters.bhk} onChange={(v) => setFilters({ ...filters, bhk: v })} options={bhkOptions} />
                )}
                <div className="space-y-1">
                  <p className="text-xs text-gray-500">Min Area (sq ft)</p>
                  <Input type="number" value={filters.minArea} onChange={(e) => setFilters({ ...filters, minArea: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-500">Max Area (sq ft)</p>
                  <Input type="number" value={filters.maxArea} onChange={(e) => setFilters({ ...filters, maxArea: e.target.value })} />
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Listings */}
        <section className="py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="mb-5 flex items-center justify-between">
              <p className="text-gray-600">
                Showing <span className="font-semibold text-gray-900">{filtered.length}</span> of {properties.length}
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand-orange" /></div>
            ) : filtered.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
                {filtered.map((p) => <PropertyCard key={p.id} property={p} />)}
              </div>
            ) : (
              <div className="text-center py-20 bg-gray-50 rounded-2xl">
                <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">{t('common.no_properties', 'No properties found')}</h3>
                <p className="text-gray-500">Try adjusting your filters.</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

const FilterSelect = ({ label, value, onChange, options }) => (
  <div className="space-y-1">
    <p className="text-xs text-gray-500">{label}</p>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);
